import React, { useEffect, useState, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import ErrorBoundary from './Components/ErrorBoundary';
import { CircularProgress, Box, ThemeProvider } from '@mui/material';
import { useAppContext } from './contexts/appcontext';
import { SocketProvider } from './contexts/SocketContext';
import theme from './theme';

// Lazy load components for code splitting
const Signup = lazy(() => import('./Auth/Signup'));
const Signin = lazy(() => import('./Auth/Login'));
const Forget = lazy(() => import('./Auth/Forget'));
const Reset = lazy(() => import('./Auth/Reset'));
const Dashboard = lazy(() => import('./Components/Dashboard'));
const Layout = lazy(() => import('./Components/Layout'));
const OrderList = lazy(() => import('./Components/Orders/OrderList'));
const OrderDetails = lazy(() => import('./Components/Orders/OrderDetails'));
const CustomerList = lazy(() => import('./Components/Customers/CustomerList'));
const CustomerDetails = lazy(() => import('./Components/Customers/CustomerDetails'));
const Organizations = lazy(() => import('./Components/Organizations/Organizations'));
const OrganizationDetails = lazy(() => import('./Components/Organizations/OrganizationDetails'));
const ProductCategories = lazy(() => import('./Auth/pages/productsCategories'));
const ProductDetails = lazy(() => import('./Auth/pages/products/productDetails'));
const ProductList = lazy(() => import('./Auth/pages/products/productListing'));
const AddProduct = lazy(() => import('./Auth/pages/products/addProduct'));
const OrganizationsDash = lazy(() => import('./Auth/pages/organizations/organizations'));
const Org_Store = lazy(() => import('./Auth/pages/organizations/store/org_store'));
const EmployeesListing = lazy(() => import('./Auth/pages/organizations/Employess/EmployeesListing'));
const EmployeeDetailing = lazy(() => import('./Auth/pages/organizations/Employess/employeeDetailing'));
const EmployeesOrderDetailing = lazy(() => import('./Auth/pages/organizations/Employess/orderDetails'));
const StoreDetails = lazy(() => import('./Auth/pages/store/store'));
const CustomerProfile = lazy(() => import('./Auth/pages/customer/customerOperations'));
const RecognitionTask = lazy(() => import('./Auth/pages/customer/recognitionTask'));
const ProductDetailsPage = lazy(() => import('./Components/organizationAdmin/productDetails/productsDetail'));
const CartPage = lazy(() => import('./Components/organizationAdmin/cartUi/cartPage'));
const DeliveryPage = lazy(() => import('./Components/organizationAdmin/cartUi/delivery'));
const OrdersPage = lazy(() => import('./Components/organizationAdmin/orderUI/orderListing'));
const OrderDetailPage = lazy(() => import('./Components/organizationAdmin/orderUI/orderDetails'));
const OrderTracking = lazy(() => import('./Components/organizationAdmin/orderUI/trackOrder'));
const CategoryPage = lazy(() => import('./Components/organizationAdmin/productDetails/categoriesType'));
const NotificationCenter = lazy(() => import('./Components/Notifications/NotificationCenter'));
const FooterSettings = lazy(() => import('./Auth/pages/products/footerSettings'));
const BannerSettings = lazy(() => import('./Auth/pages/products/bannerSettings'));
// Activity Log UI disabled - activity logging still works in background for notifications
// const ActivityLog = lazy(() => import('./Components/Activities/ActivityLog'));
// WalletDashboard removed - wallet functionality is now integrated into My Profile page

// Loading component
const LoadingSpinner = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress size={40} />
  </Box>
);

// Auth guard: redirects unauthenticated users to signin
const ProtectedRoute = () => {
  const { token } = useAppContext();
  // Fallback: read sessionStorage synchronously to avoid redirect during hydration
  const sessionToken = sessionStorage.getItem("token");
  if (!token && !sessionToken) {
    return <Navigate to="/signin" replace />;
  }
  return <Outlet />;
};


const App: React.FC = () => {
  const { roleType } = useAppContext();

  return (
    <ThemeProvider theme={theme}>
      <ErrorBoundary>
        <SocketProvider>
          <>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <BrowserRouter>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                <Route path="" element={<Signin />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/signin" element={<Signin />} />
                <Route path="/" element={<Signin />} />
                <Route path="/forgetpassword" element={<Forget />} />
                <Route path="/resetpassword" element={<Reset />} />
                <Route element={<ProtectedRoute />}>
                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/orders/list" element={<OrderList />} />
                  <Route path="/orders/details/:id" element={<OrderDetails />} />
                  <Route path="/customers/list" element={<CustomerList />} />
                  <Route path="/customers/details/:id" element={<CustomerDetails />} />
                  <Route path="/store/products" element={< ProductList />} />
                  <Route path="/store/products/addproduct" element={< AddProduct />} />
                  <Route path="/store/products/:id" element={< ProductDetails />} />
                  <Route path="/store/categories" element={< ProductCategories />} />
                  <Route path="/store/footer-settings" element={< FooterSettings />} />
                  <Route path="/store/banner-settings" element={< BannerSettings />} />
                  <Route path="/organizations" element={<Organizations />} />
                  <Route path="/organizations/details/:id" element={<OrganizationDetails />} />
                  <Route path="/notifications" element={<NotificationCenter />} />
                  {/* Activity Log UI disabled - activity logging still works in background */}
                  {/* <Route path="/activity-log" element={<ActivityLog />} /> */}
                  <Route path="/settings" element={<Dashboard />} />
                  {/* organizations  operations*/}
                  <Route path="/admin" element={<OrganizationsDash />} />
                  <Route path="/store" element={<Org_Store />} />
                  <Route path="/employees" element={<EmployeesListing />} />
                  <Route path="/employees/:id" element={<EmployeeDetailing />} />
                  <Route path="/employees/:id/:orderid" element={<EmployeesOrderDetailing />} />
                  {/* store landing page */}
                  <Route path="/store/details" element={<StoreDetails />} />
                  {/* Employee operation  */}
                  <Route path="/my-profile" element={<CustomerProfile />} />
                  <Route path="/recognition-tasks" element={<RecognitionTask />} />
                  {/* /my-wallet route removed - wallet functionality is now in My Profile (tab=3) */}
                  <Route path="/store/product-details" element={<ProductDetailsPage />} />
                  <Route path="/store/product-categories" element={<CategoryPage />} />
                  <Route path="/my-cart" element={<CartPage />} />
                  <Route path="/my-cart/delivery" element={<DeliveryPage />} />
                  <Route path="/user/orders" element={<OrdersPage />} />
                  <Route path="/user/orders-details" element={<OrderDetailPage />} />
                  <Route path="/user/orders-details/tracking" element={<OrderTracking />} />
                </Route>
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
          </>
        </SocketProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
